/**
 * Abstract base for all tool-execution errors.
 *
 * Subclass for every distinct failure mode — never instantiate this base
 * directly. The class hierarchy under `lib/tool/errors/` is the registry:
 * `ls lib/tool/errors/` enumerates every error the system can raise. The
 * class name (`error.name`, automatically derived from `this.constructor`)
 * is the wire-format discriminator (`errorType`); there is no parallel
 * string-enum to maintain.
 *
 * Three top-level branches map 1:1 to three LLM recovery strategies:
 * - {@link InvalidToolInputError} → retry with different arguments
 * - {@link DataShapeError} → inspect the data, then retry
 * - {@link EnvironmentError} → fatal, cannot be fixed by retrying
 */

import { formatErrorText } from './format.ts';

/**
 * Canonical list of recovery strategy values. Single source of truth — the
 * {@link ErrorRecoveryStrategy} type and any Zod schema that validates the
 * wire format both derive from this tuple.
 */
export const ERROR_RECOVERY_STRATEGIES = [
  /** LLM gave bad arguments. Retry with corrected arguments. */
  'retry-with-different-input',
  /** Args were structurally valid but the data didn't match. Inspect the data, then retry. */
  'inspect-data-then-retry',
  /** Tool harness misconfiguration. Cannot be fixed by retrying — report to user. */
  'fatal',
] as const;

/** Recovery strategy hint surfaced to the LLM. */
export type ErrorRecoveryStrategy = (typeof ERROR_RECOVERY_STRATEGIES)[number];

/** Structured wire shape sent to the LLM as a tool-execution failure. */
export interface ToolErrorResponse {
  success: false;
  /** Class name of the thrown error — stable, machine-readable discriminator. */
  errorType: string;
  /** Human-readable error message. */
  error: string;
  /** Actionable recovery hints for the LLM. */
  suggestions: readonly string[];
  /** Structured fields for the UI / LLM to branch on without parsing the message. */
  details: Readonly<Record<string, unknown>>;
  /** Recovery strategy hint — what should the LLM do next. */
  recovery: ErrorRecoveryStrategy;
}

export abstract class ToolExecutionError extends Error {
  /** Recovery strategy. Set on each top-level branch class. */
  abstract readonly recovery: ErrorRecoveryStrategy;

  constructor(message: string) {
    super(message);
    // The class name is the wire-format discriminator. Derive it from the
    // constructor so subclasses don't have to remember to set it manually.
    this.name = new.target.name;
    // Restore prototype chain for `instanceof` across module boundaries —
    // required when subclassing built-in `Error` in TS-compiled output.
    Object.setPrototypeOf(this, new.target.prototype);
  }

  /**
   * Actionable recovery hints for the LLM. Override per-class. Default empty.
   */
  suggestions(): readonly string[] {
    return [];
  }

  /**
   * Structured fields the UI / LLM can branch on without parsing the message
   * (e.g. lists of available column names). Override per-class when
   * referenceable structured data exists. Default empty.
   */
  details(): Readonly<Record<string, unknown>> {
    return {};
  }

  /** Convert to the wire shape consumed by the LLM and the chat runtime. */
  toStructuredResponse(): ToolErrorResponse {
    return {
      success: false,
      errorType: this.name,
      error: this.message,
      suggestions: this.suggestions(),
      details: this.details(),
      recovery: this.recovery,
    };
  }

  /**
   * Render this error as the markdown text shown to the user / LLM. Subclasses
   * with richer rendering needs (e.g. a missing-columns table instead of a
   * bullet list) can override.
   *
   * Delegates to the standalone {@link formatErrorText} so that the wire-side
   * renderer (which only has a parsed JSON envelope, not the live instance)
   * and any throw-side caller both produce identical output.
   */
  format(): string {
    return formatErrorText(this.toStructuredResponse());
  }
}
